import { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useSearchParams, Link } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-hot-toast";
import { ShieldCheck } from "lucide-react";
import { activityService } from "@/services/activityService";
import { adminService } from "@/services/adminService";
import { legalInfoQueryKey, profileService } from "@/services/profileService";
import { useAuthStore } from "@/store/authStore";
import { useUnsavedFormGuard } from "@/hooks/useUnsavedFormGuard";
import { Alert, Button, ConfirmModal } from "@/components/ui";
import {
  PageFrame,
  DetailTabPanel,
  DetailTabPanelHeading,
} from "@/components/common";
import { adminProfilePathForRole } from "@/lib/adminProfilePaths";
import { toastApiError } from "@/lib/errorMessages";
import {
  ActivityDetailTabNav,
  ActivityHeader,
  LocationSection,
  MediaSection,
  CatalogCopySection,
  RestaurantConfigSection,
  HandshakeConfigSection,
  SaveBar,
  MenuItemsSection,
  ServiceCategoriesSection,
  ServiceItemsSection,
  ScheduleSection,
  BookingCutoffSection,
  ActivityPreviewSection,
  ActivityStaffPanel,
  AdminActivityCatalogImportSection,
} from "./components";
import {
  parseActivityDetailTab,
  type ActivityDetailTabId,
} from "./activityDetailTabs";
import {
  hasActivityStaffPanelContent,
  type ActivityStaffPanelAccess,
} from "./activityStaffAccess";
import {
  ActivityCategory,
  Permission,
  UserRole,
  ActivityAvailabilityMode,
  type Activity,
  type ActivityUpdate,
  type AddressValidationState,
  type MediaItem,
} from "@/types";
import { preventImplicitSubmitOnEnter } from "@/lib/formKeyboard";
import { isLocationDirty } from "@/lib/locationForm";
import { resolveLocale } from "@/lib/locales";
import { useInvalidAddressSaveConfirm } from "@/hooks/useInvalidAddressSaveConfirm";
import {
  buildActivityUpdateSchema,
  type ActivityDetailFormValues,
} from "./activityDetailForm";
import {
  ACTIVITY_DESCRIPTION_MAX_LENGTH,
  ACTIVITY_NAME_MAX_LENGTH,
  buildItemDescriptionTranslations,
  buildRequiredTranslations,
  findAutoTranslateSourceLanguage,
  findMissingTranslations,
  findTranslationOverMaxLength,
  hasPartialTranslations,
  areTranslationsEqual,
  type TranslationFormValues,
} from "@/lib/translationResolution";
import {
  applyAutoTranslateResult,
  useCatalogAutoTranslate,
} from "@/hooks/useCatalogAutoTranslate";
import {
  cutoffFieldsToMinutes,
  minutesToCutoffFields,
} from "./bookingCutoffForm";

function buildActivityFormDefaults(
  activity: Activity,
): ActivityDetailFormValues {
  const cutoff = minutesToCutoffFields(activity.booking_cutoff_minutes);
  return {
    address: activity.address || "",
    coordinates: activity.coordinates ?? null,
    avg_price_per_person: activity.avg_price_per_person?.toString() || "",
    requires_handshake: activity.requires_handshake || false,
    booking_cutoff_value: cutoff.value,
    booking_cutoff_unit: cutoff.unit,
    is_visible: activity.is_visible,
  };
}

function initialNameTranslations(activity: Activity): TranslationFormValues {
  return { ...activity.name_translations };
}

interface ActivityDetailEditorProps {
  activity: Activity;
  sellerProfileIncomplete: boolean;
  /** Staff editing another user's activity (vs. own listing). */
  managingOthersActivity: boolean;
  partnerPath: string | null;
  staffAccess: ActivityStaffPanelAccess;
  showCatalogImport: boolean;
}

function ActivityDetailEditor({
  activity,
  sellerProfileIncomplete,
  managingOthersActivity,
  partnerPath,
  staffAccess,
  showCatalogImport,
}: ActivityDetailEditorProps) {
  const { t, i18n } = useTranslation("activities");
  const { t: tNav } = useTranslation("nav");
  const { t: tCommon } = useTranslation("common");
  const { t: tValidation } = useTranslation("validation");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const activityId = activity.id;
  const [showProfileIncompleteModal, setShowProfileIncompleteModal] = useState(
    sellerProfileIncomplete,
  );

  const [media, setMedia] = useState<MediaItem[]>(() => activity.media || []);
  const [cuisineTypes, setCuisineTypes] = useState<string[]>(
    () => activity.cuisine_types || [],
  );
  const [descriptionTranslations, setDescriptionTranslations] =
    useState<TranslationFormValues>(() => ({
      ...(activity.description_translations ?? {}),
    }));
  const [descriptionError, setDescriptionError] = useState<
    string | undefined
  >();
  const [nameTranslations, setNameTranslations] =
    useState<TranslationFormValues>(() => initialNameTranslations(activity));
  const [nameError, setNameError] = useState<string | undefined>();

  const updateSchema = useMemo(() => buildActivityUpdateSchema(t), [t]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty, dirtyFields },
  } = useForm<ActivityDetailFormValues>({
    resolver: zodResolver(updateSchema),
    defaultValues: buildActivityFormDefaults(activity),
  });

  const [addressValidation, setAddressValidation] =
    useState<AddressValidationState>({ status: "idle" });

  useEffect(() => {
    if (!sellerProfileIncomplete) {
      return;
    }
    setValue("is_visible", false, { shouldDirty: false });
  }, [sellerProfileIncomplete, setValue]);

  const cuisineTypesChanged =
    JSON.stringify(cuisineTypes) !==
    JSON.stringify(activity.cuisine_types || []);
  const descriptionTranslationsChanged = !areTranslationsEqual(
    descriptionTranslations,
    activity.description_translations,
  );
  const nameTranslationsChanged = !areTranslationsEqual(
    nameTranslations,
    activity.name_translations,
  );
  const hasUnsavedChanges =
    isDirty ||
    cuisineTypesChanged ||
    descriptionTranslationsChanged ||
    nameTranslationsChanged;

  const nameModified = nameTranslationsChanged;
  const visibilityModified = !!dirtyFields.is_visible;
  const locationModified = isLocationDirty(dirtyFields);
  const restaurantConfigModified =
    cuisineTypesChanged || !!dirtyFields.avg_price_per_person;
  const handshakeConfigModified = !!dirtyFields.requires_handshake;
  const { saveBarPhase, notifySaved } = useUnsavedFormGuard(hasUnsavedChanges);
  const showStaffTab = hasActivityStaffPanelContent(staffAccess);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseActivityDetailTab(
    searchParams.get("tab"),
    showStaffTab,
  );
  const setActiveTab = useCallback(
    (tab: ActivityDetailTabId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === "general") {
            next.delete("tab");
          } else {
            next.set("tab", tab);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const preferredSourceLanguage = resolveLocale(i18n.language);
  const autoTranslateSourceLanguage =
    findAutoTranslateSourceLanguage(
      nameTranslations,
      preferredSourceLanguage,
    ) ?? preferredSourceLanguage;

  const autoTranslate = useCatalogAutoTranslate({
    activityId,
    sourceLanguage: autoTranslateSourceLanguage,
    nameTranslations,
    descriptionTranslations,
    requireDescription: false,
    onApplied: (result) => {
      const applied = applyAutoTranslateResult(
        nameTranslations,
        descriptionTranslations,
        result,
        { mergeDescriptions: true },
      );
      setNameTranslations(applied.nameTranslations);
      setDescriptionTranslations(applied.descriptionTranslations);
      setNameError(undefined);
      setDescriptionError(undefined);
    },
    onMissingName: () => {
      setNameError(t("zod.nameRequired"));
      setActiveTab("general");
    },
  });

  const handleRevert = useCallback(() => {
    const baseline =
      queryClient.getQueryData<Activity>(["activity", activityId]) ?? activity;
    reset(buildActivityFormDefaults(baseline));
    setMedia(baseline.media || []);
    setCuisineTypes(baseline.cuisine_types || []);
    setDescriptionTranslations({
      ...(baseline.description_translations ?? {}),
    });
    setNameTranslations(initialNameTranslations(baseline));
    setDescriptionError(undefined);
    setNameError(undefined);
  }, [queryClient, activityId, activity, reset]);

  const handleMediaAutoSave = useCallback(
    async (newMedia: MediaItem[]) => {
      const updated = await activityService.update(activityId, {
        media: newMedia,
      });
      queryClient.setQueryData<Activity>(["activity", activityId], updated);
      setMedia(updated.media || []);
      queryClient.invalidateQueries({ queryKey: ["activities"] });
    },
    [activityId, queryClient],
  );

  const updateMutation = useMutation({
    mutationFn: (data: ActivityUpdate) =>
      activityService.update(activityId, data),
    onSuccess: (updated) => {
      queryClient.setQueryData<Activity>(["activity", activityId], updated);
      reset(buildActivityFormDefaults(updated));
      setMedia(updated.media || []);
      setCuisineTypes(updated.cuisine_types || []);
      setDescriptionTranslations({
        ...(updated.description_translations ?? {}),
      });
      setNameTranslations(initialNameTranslations(updated));

      queryClient.invalidateQueries({ queryKey: ["activities"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "seller"] });

      toast.success(t("detail.updatedToast"));
      notifySaved();
    },
    onError: (err) => {
      toastApiError(err, "updateActivity");
    },
  });

  const { requestInvalidAddressSaveConfirm, confirmModal } =
    useInvalidAddressSaveConfirm();

  const handleDescriptionTranslationsChange = (
    values: TranslationFormValues,
  ) => {
    setDescriptionTranslations(values);
    if (descriptionError) {
      setDescriptionError(undefined);
    }
  };

  const handleNameTranslationsChange = (values: TranslationFormValues) => {
    setNameTranslations(values);
    if (nameError) {
      setNameError(undefined);
    }
  };

  const submitActivityUpdate = async (data: ActivityDetailFormValues) => {
    const names = nameTranslations;
    const descriptions = descriptionTranslations;
    const activating = sellerProfileIncomplete ? false : data.is_visible;

    if (findMissingTranslations(names).length > 0) {
      setNameError(t("zod.allTranslationsRequired"));
      setActiveTab("general");
      return;
    }
    if (activating && findMissingTranslations(descriptions).length > 0) {
      setDescriptionError(t("zod.descriptionRequired"));
      setActiveTab("general");
      return;
    }
    if (!activating && hasPartialTranslations(descriptions)) {
      setDescriptionError(t("zod.descriptionRequired"));
      setActiveTab("general");
      return;
    }
    if (findTranslationOverMaxLength(names, ACTIVITY_NAME_MAX_LENGTH)) {
      setNameError(tValidation("maxChars", { max: ACTIVITY_NAME_MAX_LENGTH }));
      setActiveTab("general");
      return;
    }
    if (
      findTranslationOverMaxLength(
        descriptions,
        ACTIVITY_DESCRIPTION_MAX_LENGTH,
      )
    ) {
      setDescriptionError(
        tValidation("maxChars", { max: ACTIVITY_DESCRIPTION_MAX_LENGTH }),
      );
      setActiveTab("general");
      return;
    }

    const updateData: ActivityUpdate = {
      name_translations: buildRequiredTranslations(names),
      description_translations: buildItemDescriptionTranslations(descriptions),
      media,
      cuisine_types: cuisineTypes.length > 0 ? cuisineTypes : undefined,
      avg_price_per_person: data.avg_price_per_person
        ? Number(data.avg_price_per_person)
        : undefined,
      requires_handshake: data.requires_handshake,
      is_visible: activating,
    };

    if (activity.availability_mode === ActivityAvailabilityMode.SCHEDULED) {
      updateData.booking_cutoff_minutes = cutoffFieldsToMinutes(
        data.booking_cutoff_value,
        data.booking_cutoff_unit,
      );
    }

    updateData.address = data.address || "";
    updateData.city = "";
    updateData.postal_code = "";
    updateData.country = "";
    updateData.coordinates = data.coordinates;

    updateMutation.mutate(updateData);
  };

  const handleFormSubmit = (data: ActivityDetailFormValues) => {
    requestInvalidAddressSaveConfirm(
      true,
      addressValidation,
      tCommon("addressValidation.saveWarningActivity"),
      () => submitActivityUpdate(data),
    );
  };

  const formId = `activity-profile-${activityId}`;

  const isListed = sellerProfileIncomplete ? false : watch("is_visible");

  return (
    <div
      className="w-full min-w-0"
      onKeyDown={preventImplicitSubmitOnEnter}
    >
      <ConfirmModal
        closeLabel={tCommon("close")}

        isOpen={showProfileIncompleteModal}
        onClose={() => setShowProfileIncompleteModal(false)}
        onConfirm={() => {
          setShowProfileIncompleteModal(false);
          navigate("/settings");
        }}
        title={t("detail.profileIncompleteModalTitle")}
        message={t("detail.profileIncompleteModalMessage")}
        confirmText={t("detail.profileIncompleteModalGoToProfile")}
        cancelText={t("detail.profileIncompleteModalLater")}
      />
      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
        confirmText={tCommon("confirm")}
        isOpen={autoTranslate.confirmOpen}
        onClose={autoTranslate.onConfirmClose}
        onConfirm={autoTranslate.onConfirm}
        title={autoTranslate.confirmTitle}
        message={autoTranslate.confirmMessage}
        isLoading={autoTranslate.isPending}
      />
      <form
        id={formId}
        className="w-full min-w-0"
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <div className="mb-3 space-y-3">
          <ActivityHeader
            activity={activity}
            nameTranslations={nameTranslations}
            register={register}
            watch={watch}
            visibilityModified={visibilityModified}
            activationLocked={sellerProfileIncomplete}
            staffMode={managingOthersActivity}
            partnerPath={partnerPath}
          />

          {sellerProfileIncomplete ? (
            <Alert variant="warning">
              <p>{t("detail.profileIncompleteBanner")}</p>
              <Link
                to="/settings"
                className="text-primary-600 hover:text-primary-700 mt-2 inline-block text-sm font-medium hover:underline"
              >
                {tNav("completeProfileLink")}
              </Link>
            </Alert>
          ) : null}

          {!activity.is_active ? (
            <Alert
              variant="warning"
              action={
                staffAccess.active && activeTab !== "staff" ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    leftIcon={<ShieldCheck className="h-4 w-4" />}
                    onClick={() => setActiveTab("staff")}
                  >
                    {t("detail.openStaffTab")}
                  </Button>
                ) : undefined
              }
            >
              {t("detail.inactiveAdminBanner")}
            </Alert>
          ) : !isListed && !sellerProfileIncomplete ? (
            <Alert variant="warning">{t("detail.hiddenBanner")}</Alert>
          ) : null}
        </div>

        <ActivityDetailTabNav
          activeTab={activeTab}
          onTabChange={setActiveTab}
          showStaffTab={showStaffTab}
        />
        <DetailTabPanel
          id="activity-tabpanel-general"
          labelledBy="activity-tab-general"
          isActive={activeTab === "general"}
        >
          <DetailTabPanelHeading
            title={t("detail.tabs.generalHeading")}
            hint={t("detail.tabs.generalHint")}
          />

          <CatalogCopySection
            nameTranslations={nameTranslations}
            onNameTranslationsChange={handleNameTranslationsChange}
            descriptionTranslations={descriptionTranslations}
            onDescriptionTranslationsChange={
              handleDescriptionTranslationsChange
            }
            nameError={nameError}
            descriptionError={descriptionError}
            modified={nameModified || descriptionTranslationsChanged}
            descriptionRequired={isListed}
            onTranslate={autoTranslate.run}
            isTranslating={autoTranslate.isPending}
            translateDisabled={updateMutation.isPending}
          />

          <LocationSection
            activity={activity}
            setValue={setValue}
            watch={watch}
            errors={errors}
            modified={locationModified}
            onValidationChange={setAddressValidation}
          />

          <MediaSection
            media={media}
            activityId={activityId}
            onChange={setMedia}
            onAutoSave={handleMediaAutoSave}
          />
        </DetailTabPanel>

        <DetailTabPanel
          id="activity-tabpanel-configuration"
          labelledBy="activity-tab-configuration"
          isActive={activeTab === "configuration"}
        >
          <DetailTabPanelHeading
            title={t("detail.tabs.configurationHeading")}
            hint={t("detail.tabs.configurationHint")}
          />

          <div className="mb-6 grid gap-6 xl:grid-cols-2 xl:items-start">
            <div className="min-w-0 space-y-6">
              <HandshakeConfigSection
                activityCategory={activity.category}
                register={register}
                watch={watch}
                modified={handshakeConfigModified}
              />
              {activity.category === ActivityCategory.RESTAURANT ? (
                <RestaurantConfigSection
                  cuisineTypes={cuisineTypes}
                  onCuisineTypesChange={setCuisineTypes}
                  register={register}
                  errors={errors}
                  modified={restaurantConfigModified}
                />
              ) : null}
            </div>
            <div className="min-w-0">
              <ServiceCategoriesSection
                activityId={activityId}
                categories={activity.service_categories || []}
                includeDefaultMenuCourses={
                  activity.category === ActivityCategory.RESTAURANT
                }
              />
            </div>
          </div>

          {showCatalogImport ? (
            <AdminActivityCatalogImportSection
              key={`catalog-import-${activity.id}`}
              activity={activity}
            />
          ) : null}

          {activity.category === ActivityCategory.RESTAURANT ? (
            <MenuItemsSection
              activityId={activityId}
              activityCommissionRate={activity.commission_rate}
              serviceCategories={activity.service_categories || []}
            />
          ) : (
            <ServiceItemsSection
              activityId={activityId}
              activityCommissionRate={activity.commission_rate}
              serviceCategories={activity.service_categories || []}
            />
          )}
        </DetailTabPanel>
      </form>

      <DetailTabPanel
        id="activity-tabpanel-availability"
        labelledBy="activity-tab-availability"
        isActive={activeTab === "availability"}
      >
        <DetailTabPanelHeading
          title={t("detail.tabs.availabilityHeading")}
          hint={t("detail.tabs.availabilityHint")}
        />

        <ScheduleSection
          activityId={activityId}
          activityType={activity.category}
        />

        {activity.availability_mode === ActivityAvailabilityMode.SCHEDULED && (
          <BookingCutoffSection
            formId={formId}
            register={register}
            errors={errors}
            modified={
              !!dirtyFields.booking_cutoff_value ||
              !!dirtyFields.booking_cutoff_unit
            }
          />
        )}
      </DetailTabPanel>

      <DetailTabPanel
        id="activity-tabpanel-preview"
        labelledBy="activity-tab-preview"
        isActive={activeTab === "preview"}
      >
        {activeTab === "preview" ? (
          <ActivityPreviewSection activityId={activityId} />
        ) : null}
      </DetailTabPanel>

      {showStaffTab ? (
        <DetailTabPanel
          id="activity-tabpanel-staff"
          labelledBy="activity-tab-staff"
          isActive={activeTab === "staff"}
        >
          <DetailTabPanelHeading
            title={t("detail.tabs.staffHeading")}
            hint={t("detail.tabs.staffHint")}
          />
          <ActivityStaffPanel
            activity={activity}
            access={staffAccess}
          />
        </DetailTabPanel>
      ) : null}

      <SaveBar
        phase={saveBarPhase}
        isPending={updateMutation.isPending}
        formId={formId}
        onRevert={handleRevert}
      />
      {confirmModal}
    </div>
  );
}

export default function ActivityDetailPage() {
  const { t } = useTranslation("activities");
  const { id } = useParams<{ id: string }>();
  const { user, getCurrentUserId, hasPermission } = useAuthStore();

  const {
    data: activity,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["activity", id],
    queryFn: () => activityService.getById(id!),
    enabled: !!id,
  });

  const currentUserId = getCurrentUserId();
  const managingOthersActivity =
    !!currentUserId &&
    hasPermission(Permission.ACTIVITIES_MANAGE_ANY) &&
    !!activity &&
    activity.user_id !== currentUserId;

  const backFallback = managingOthersActivity
    ? "/admin/activities"
    : "/activities";

  const canConfigure = hasPermission(Permission.ACTIVITIES_CONFIG_UPDATE);
  const staffAccess: ActivityStaffPanelAccess = {
    active: canConfigure,
    commission: managingOthersActivity && canConfigure,
    category: managingOthersActivity && canConfigure,
    salesAgent:
      managingOthersActivity &&
      hasPermission(Permission.SALES_AGENTS_MANAGE_ANY),
    promo:
      managingOthersActivity &&
      hasPermission(Permission.ACTIVITIES_MANAGE_ANY) &&
      canConfigure,
  };
  const showCatalogImport = hasPermission(Permission.ACTIVITIES_CATALOG_IMPORT);

  const ownerUserId = activity?.user_id;

  const { data: ownerLookup } = useQuery({
    queryKey: ["admin", "users", "role", ownerUserId],
    queryFn: () => adminService.getUserRole(ownerUserId!),
    enabled: managingOthersActivity && !!ownerUserId,
  });

  const partnerPath =
    ownerUserId && ownerLookup
      ? adminProfilePathForRole(ownerUserId, ownerLookup.role)
      : null;

  const isOwnSellerActivity =
    !!currentUserId &&
    user?.role === UserRole.SELLER &&
    !!activity &&
    activity.user_id === currentUserId &&
    !managingOthersActivity;

  const { data: sellerLegalProfile, isLoading: sellerLegalProfileLoading } =
    useQuery({
      queryKey: legalInfoQueryKey(currentUserId!),
      queryFn: () => profileService.getProfile(currentUserId!),
      enabled: isOwnSellerActivity && !!currentUserId,
    });

  const sellerProfileIncomplete =
    isOwnSellerActivity &&
    !sellerLegalProfileLoading &&
    sellerLegalProfile !== undefined &&
    !sellerLegalProfile.profile_completed;

  if (isLoading) {
    return (
      <PageFrame
        backLink={{
          fallback: backFallback,
          className: "font-normal text-surface-500 hover:text-surface-700",
          children: t("detail.back"),
        }}
        isLoading
        loadingMessage={t("detail.loading")}
      />
    );
  }

  if (error || !activity) {
    return (
      <PageFrame
        backLink={{
          fallback: backFallback,
          className: "font-normal text-surface-500 hover:text-surface-700",
          children: t("detail.back"),
        }}
        error
        errorMessage={t("detail.notFoundMessage")}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <PageFrame
      backLink={{
        fallback: backFallback,
        className: "font-normal text-surface-500 hover:text-surface-700",
        children: t("detail.back"),
      }}
    >
      <ActivityDetailEditor
        key={`${activity.id}-${sellerProfileIncomplete ? "incomplete" : "complete"}`}
        activity={activity}
        sellerProfileIncomplete={sellerProfileIncomplete}
        managingOthersActivity={managingOthersActivity}
        partnerPath={partnerPath}
        staffAccess={staffAccess}
        showCatalogImport={showCatalogImport}
      />
    </PageFrame>
  );
}
